/**
 * Config-driven accessibility widget.
 *
 * Every option comes from config/accessibility.php via window.accessibilitySettings
 * ({ state, config: { toggles, filterGroup, behaviors, steppers, profiles }, urls, t }),
 * emitted by resources/views/entities/accessibility-widget.blade.php. Adding an
 * option is a config + lang + SCSS change — nothing here needs editing.
 *
 * Effects:
 *   - toggles  -> a body class (+ an optional JS "behavior" module)
 *   - steppers -> inline CSS on the text tag list (font-size / line-height / letter-spacing)
 *   - profiles -> apply a preset bundle of the above
 * State persists to the session via the preferences/reset endpoints.
 *
 * Also drives the two dialogs the panel can open: the accessibility statement
 * (admin-authored HTML) and the "report a problem" form, which posts to
 * accessibility.complaint.
 *
 * Plain jQuery IIFE rather than an ES class: this bundle is loaded on its own
 * with <script src>, after a global jQuery, on public card pages that carry no
 * other module bundle.
 */
(function ($) {
    // Elements that receive the inline text steppers (font / line / letter).
    const FONT_TAGS = 'body, button, a, strong, h1, h2, h3, h4, h5, h6, span, p, i, label, input, textarea, select, li, td, th, legend';

    $(document).ready(function () {
        const settings = window.accessibilitySettings;
        if (!settings || !document.getElementById('accessibility-widget')) {
            return;
        }

        const cfg         = settings.config || {};
        const toggles     = cfg.toggles || {};      // pref key -> body class
        const steppers    = cfg.steppers || {};     // pref key -> meta
        const profiles    = cfg.profiles || {};     // profile key -> bundle
        const filterGroup = cfg.filterGroup || [];
        const behaviors   = cfg.behaviors || [];
        const urls        = settings.urls || {};
        const t           = settings.t || {};
        const labels      = t.labels || {};

        // Working copy of the persisted state; backfill missing steppers.
        const state = Object.assign({}, settings.state || {});
        Object.keys(steppers).forEach(function (k) {
            if (typeof state[k] !== 'number') {
                state[k] = steppers[k].default;
            }
        });

        const $panel     = $('#accessibility-panel');
        const $toggleBtn = $('#accessibility-toggle');
        const $announcer = $('#accessibility-announcer');

        /* ---- behavior modules (tooltips / ruler / mask) ---------------- */

        function makeTooltips() {
            let el = null, active = false;
            const widget = document.getElementById('accessibility-widget');

            function desc(node) {
                return node.getAttribute('title') || node.getAttribute('aria-label') ||
                    (node.tagName === 'IMG' ? node.getAttribute('alt') : '') || '';
            }
            function ensure() {
                if (el) return el;
                el = document.createElement('div');
                el.className = 'a11y-tooltip';
                el.setAttribute('role', 'tooltip');
                document.body.appendChild(el);
                return el;
            }
            function hide() { if (el) el.classList.remove('is-visible'); }
            function show(e) {
                const node = e.target.closest ? e.target.closest('[title],[aria-label],img[alt]') : null;
                if (!node || (widget && widget.contains(node))) { hide(); return; }
                const text = desc(node);
                if (!text) { hide(); return; }
                const box = ensure();
                box.textContent = text;
                const r = node.getBoundingClientRect();
                box.style.top = (window.scrollY + r.top - box.offsetHeight - 8) + 'px';
                box.style.left = (window.scrollX + r.left) + 'px';
                box.classList.add('is-visible');
            }

            return {
                enable: function (on) {
                    if (on === active) return;
                    active = on;
                    if (on) {
                        document.addEventListener('mouseover', show, true);
                        document.addEventListener('focusin', show, true);
                        document.addEventListener('mouseout', hide, true);
                        document.addEventListener('focusout', hide, true);
                    } else {
                        document.removeEventListener('mouseover', show, true);
                        document.removeEventListener('focusin', show, true);
                        document.removeEventListener('mouseout', hide, true);
                        document.removeEventListener('focusout', hide, true);
                        hide();
                    }
                }
            };
        }

        function makeRuler() {
            let bar = null, active = false;
            function move(e) {
                if (!bar) return;
                const y = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY;
                if (typeof y !== 'number') return;
                bar.style.top = y + 'px';
            }
            return {
                enable: function (on) {
                    if (on === active) return;
                    active = on;
                    if (on) {
                        if (!bar) {
                            bar = document.createElement('div');
                            bar.className = 'a11y-ruler';
                            bar.setAttribute('aria-hidden', 'true');
                            document.body.appendChild(bar);
                        }
                        bar.style.display = 'block';
                        document.addEventListener('mousemove', move, true);
                        document.addEventListener('touchmove', move, true);
                    } else {
                        document.removeEventListener('mousemove', move, true);
                        document.removeEventListener('touchmove', move, true);
                        if (bar) bar.style.display = 'none';
                    }
                }
            };
        }

        function makeMask() {
            let top = null, bottom = null, active = false;
            const BAND = 90;
            function move(e) {
                if (!top) return;
                const y = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY;
                if (typeof y !== 'number') return;
                top.style.height = Math.max(0, y - BAND / 2) + 'px';
                bottom.style.top = Math.min(window.innerHeight, y + BAND / 2) + 'px';
            }
            return {
                enable: function (on) {
                    if (on === active) return;
                    active = on;
                    if (on) {
                        if (!top) {
                            top = document.createElement('div');
                            top.className = 'a11y-mask a11y-mask--top';
                            top.setAttribute('aria-hidden', 'true');
                            bottom = document.createElement('div');
                            bottom.className = 'a11y-mask a11y-mask--bottom';
                            bottom.setAttribute('aria-hidden', 'true');
                            document.body.appendChild(top);
                            document.body.appendChild(bottom);
                        }
                        top.style.display = bottom.style.display = 'block';
                        document.addEventListener('mousemove', move, true);
                        document.addEventListener('touchmove', move, true);
                    } else {
                        document.removeEventListener('mousemove', move, true);
                        document.removeEventListener('touchmove', move, true);
                        if (top) top.style.display = bottom.style.display = 'none';
                    }
                }
            };
        }

        const modules = {
            tooltips: makeTooltips(),
            reading_ruler: makeRuler(),
            reading_mask: makeMask()
        };

        const __accessibility = {
            /* ---- open / close ----------------------------------------- */
            isPanelHidden: function () {
                return $panel.attr('hidden') !== undefined;
            },
            toggle: function () {
                if (this.isPanelHidden()) {
                    this.open();
                } else {
                    this.close();
                }
            },
            open: function () {
                $panel.removeAttr('hidden');
                $toggleBtn.attr('aria-expanded', 'true');
                setTimeout(function () { $('#close-panel').trigger('focus'); }, 100);
                this.announce(t.opened);
            },
            close: function () {
                $panel.attr('hidden', '');
                $toggleBtn.attr('aria-expanded', 'false').trigger('focus');
                this.announce(t.closed);
            },
            closePress: function (e) {
                if (this.isPanelHidden()) return;
                // A click inside a dialog is inside the widget, so the panel stays.
                if (!$(e.target).closest('#accessibility-widget').length) {
                    $panel.attr('hidden', '');
                    $toggleBtn.attr('aria-expanded', 'false');
                }
            },
            handleEscape: function (e) {
                if (e.key !== 'Escape') return;
                // A dialog on top of the panel takes the Escape first.
                const $openDialog = $('.a11y-dialog').not('[hidden]');
                if ($openDialog.length) {
                    this.closeDialog($openDialog.attr('id'));
                    return;
                }
                if (!this.isPanelHidden()) {
                    this.close();
                }
            },
            trapFocus: function (e) {
                if (e.key !== 'Tab') return;
                const focusable = $panel.find('button:visible, [href]:visible, input:visible, select:visible, textarea:visible, [tabindex]:not([tabindex="-1"]):visible');
                if (!focusable.length) return;
                const first = focusable.first()[0];
                const last = focusable.last()[0];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            },
            announce: function (message) {
                if (!message) return;
                $announcer.text(message);
                setTimeout(function () { $announcer.text(''); }, 1000);
            },

            /* ---- dialogs (statement / complaint) ----------------------- */
            lastDialogTrigger: null,
            openDialog: function (id, trigger) {
                const $dialog = $('#' + id);
                if (!$dialog.length) return;
                this.lastDialogTrigger = trigger || null;
                $dialog.removeAttr('hidden');
                // Focus the first field when there is one, else the close button.
                const $focusTarget = $dialog.find('input:visible, textarea:visible').first();
                const $fallback = $dialog.find('.close-btn').first();
                setTimeout(function () {
                    ($focusTarget.length ? $focusTarget : $fallback).trigger('focus');
                }, 50);
            },
            closeDialog: function (id) {
                const $dialog = $('#' + id);
                if (!$dialog.length) return;
                $dialog.attr('hidden', '');
                if (this.lastDialogTrigger) {
                    $(this.lastDialogTrigger).trigger('focus');
                    this.lastDialogTrigger = null;
                }
            },

            /* ---- apply state to the page ------------------------------- */
            applyState: function () {
                const self = this;
                Object.keys(toggles).forEach(function (key) { self.applyToggle(key); });
                this.applySteppers();
            },
            applyToggle: function (key) {
                const on = !!state[key];
                $('body').toggleClass(toggles[key], on);
                if (behaviors.indexOf(key) !== -1 && modules[key]) {
                    modules[key].enable(on);
                }
            },
            applySteppers: function () {
                const css = {};
                Object.keys(steppers).forEach(function (key) {
                    const meta = steppers[key];
                    const val = state[key];
                    css[meta.css] = (val === meta.default) ? '' : (meta.base + val * meta.factor) + (meta.unit || '');
                });
                $(FONT_TAGS).css(css);
            },

            /* ---- mutations --------------------------------------------- */
            toggleFeature: function (key) {
                if (!(key in toggles)) return;
                const willActivate = !state[key];
                const self = this;

                // Color filters can't stack — enabling one clears the others.
                if (willActivate && filterGroup.indexOf(key) !== -1) {
                    filterGroup.forEach(function (k) {
                        if (k !== key && state[k]) {
                            state[k] = 0;
                            self.applyToggle(k);
                        }
                    });
                }

                state[key] = willActivate ? 1 : 0;
                this.applyToggle(key);
                this.syncControls();
                this.savePreferences();
                this.announce((labels[key] || '') + ' ' + (willActivate ? t.on : t.off));
            },
            step: function (key, dir) {
                const meta = steppers[key];
                if (!meta) return;
                const next = state[key] + (dir === 'inc' ? meta.step : -meta.step);
                state[key] = Math.max(meta.min, Math.min(meta.max, next));
                this.applySteppers();
                this.syncControls();
                this.savePreferences();
                this.announce((labels[key] || '') + ' ' + (meta.show_value ? state[key] + '%' : state[key]));
            },
            clearState: function () {
                Object.keys(toggles).forEach(function (k) { state[k] = 0; });
                Object.keys(steppers).forEach(function (k) { state[k] = steppers[k].default; });
            },
            resetAll: function () {
                this.clearState();
                this.applyState();
                this.syncControls();
                this.ajax(urls.reset, { _token: this.csrf() });
                this.announce(t.resetAll);
            },

            /* ---- profiles ---------------------------------------------- */
            applyProfile: function (key) {
                const p = profiles[key];
                if (!p) return;
                const wasActive = this.activeProfile() === key;
                this.clearState();
                if (!wasActive) {
                    (p.toggles || []).forEach(function (k) { if (k in toggles) state[k] = 1; });
                    const st = p.steppers || {};
                    Object.keys(st).forEach(function (k) {
                        const meta = steppers[k];
                        if (meta) state[k] = Math.max(meta.min, Math.min(meta.max, st[k]));
                    });
                }
                this.applyState();
                this.syncControls();
                this.savePreferences();
                this.announce(wasActive
                    ? t.profileCleared
                    : ((labels[key] || '') + ' — ' + t.profileApplied));
            },
            activeProfile: function () {
                const self = this;
                let found = null;
                Object.keys(profiles).forEach(function (key) {
                    if (self.profileMatches(key)) found = key;
                });
                return found;
            },
            profileMatches: function (key) {
                const p = profiles[key];
                const wantToggles = (p.toggles || []).filter(function (k) { return k in toggles; });
                const wantSteppers = {};
                Object.keys(p.steppers || {}).forEach(function (k) {
                    if (steppers[k]) wantSteppers[k] = p.steppers[k];
                });
                if (!wantToggles.length && !Object.keys(wantSteppers).length) return false;

                let ok = true;
                Object.keys(toggles).forEach(function (k) {
                    const want = wantToggles.indexOf(k) !== -1 ? 1 : 0;
                    if ((state[k] ? 1 : 0) !== want) ok = false;
                });
                Object.keys(steppers).forEach(function (k) {
                    const want = (k in wantSteppers) ? wantSteppers[k] : steppers[k].default;
                    if (state[k] !== want) ok = false;
                });
                return ok;
            },

            /* ---- controls sync ----------------------------------------- */
            syncControls: function () {
                Object.keys(toggles).forEach(function (key) {
                    const $btn = $('[data-a11y-toggle="' + key + '"]');
                    if (!$btn.length) return;
                    const on = !!state[key];
                    $btn.toggleClass('active', on)
                        .attr('aria-checked', on ? 'true' : 'false')
                        .find('.status').text(on ? (t.on || 'On') : (t.off || 'Off'));
                });

                Object.keys(steppers).forEach(function (key) {
                    const meta = steppers[key];
                    const val = state[key];
                    $('[data-a11y-step-value="' + key + '"]').text(meta.show_value ? val + '%' : val);
                    $('[data-a11y-step="' + key + '"][data-a11y-dir="dec"]').prop('disabled', val <= meta.min);
                    $('[data-a11y-step="' + key + '"][data-a11y-dir="inc"]').prop('disabled', val >= meta.max);
                });

                const ap = this.activeProfile();
                $('[data-a11y-profile]').each(function (i, el) {
                    const isActive = el.getAttribute('data-a11y-profile') === ap;
                    $(el).toggleClass('active', isActive).attr('aria-pressed', isActive ? 'true' : 'false');
                });
            },

            /* ---- complaint form ---------------------------------------- */
            submitComplaint: function (form) {
                const self = this;
                const $form = $(form);
                const $feedback = $('#a11y-complaint-feedback');
                const $submit = $form.find('.a11y-submit-btn');

                function feedback(message, isError) {
                    $feedback.text(message || '')
                        .toggleClass('is-error', !!isError)
                        .removeAttr('hidden');
                }

                // Native validity first, so the browser's own messages (which
                // screen readers already announce) do the required-field work.
                if (form.checkValidity && !form.checkValidity()) {
                    if (form.reportValidity) form.reportValidity();
                    return;
                }

                $submit.prop('disabled', true);
                feedback(t.sending, false);

                const data = $form.serializeArray().reduce(function (acc, field) {
                    acc[field.name] = field.value;
                    return acc;
                }, {});
                data._token = this.csrf();

                $.ajax({ url: urls.complaint, method: 'POST', data: data })
                    .done(function (res) {
                        const message = (res && res.message) ? res.message : '';
                        feedback(message, false);
                        self.announce(message);
                        form.reset();
                        // Leave the confirmation on screen: the response carries a
                        // case number the visitor may want to keep.
                    })
                    .fail(function (xhr) {
                        let message = t.sendFailed;
                        const res = xhr.responseJSON;
                        if (res) {
                            if (res.message) {
                                message = res.message;
                            } else if (res.errors) {
                                const first = Object.keys(res.errors)[0];
                                if (first) message = [].concat(res.errors[first])[0];
                            }
                        }
                        feedback(message, true);
                        self.announce(message);
                    })
                    .always(function () {
                        $submit.prop('disabled', false);
                    });
            },

            /* ---- persistence ------------------------------------------- */
            savePreferences: function () {
                const data = { _token: this.csrf() };
                Object.keys(toggles).forEach(function (k) { data[k] = state[k] ? 1 : 0; });
                Object.keys(steppers).forEach(function (k) { data[k] = state[k]; });
                this.ajax(urls.preferences, data);
            },
            csrf: function () {
                return $('meta[name="csrf-token"]').attr('content');
            },
            ajax: function (url, data) {
                if (!url) return;
                const self = this;
                $.ajax({ url: url, method: 'POST', data: data }).fail(function () {
                    self.announce(t.error);
                });
            },

            init: function () {
                this.applyState();
                this.syncControls();
            }
        };

        /* ---- events ---------------------------------------------------- */
        $(document).on('click', '#accessibility-toggle', function (e) { e.preventDefault(); __accessibility.toggle(); });
        $(document).on('click', '#close-panel', function (e) { e.preventDefault(); __accessibility.close(); });
        $(document).on('click', function (e) { __accessibility.closePress(e); });
        $(document).on('keydown', function (e) { __accessibility.handleEscape(e); });

        $(document).on('click', '[data-a11y-toggle]', function (e) {
            e.preventDefault();
            __accessibility.toggleFeature($(this).attr('data-a11y-toggle'));
        });
        $(document).on('click', '[data-a11y-step]', function (e) {
            e.preventDefault();
            __accessibility.step($(this).attr('data-a11y-step'), $(this).attr('data-a11y-dir'));
        });
        $(document).on('click', '[data-a11y-profile]', function (e) {
            e.preventDefault();
            __accessibility.applyProfile($(this).attr('data-a11y-profile'));
        });
        $(document).on('click', "[data-a11y-action='reset-all']", function (e) {
            e.preventDefault();
            __accessibility.resetAll();
        });

        // Dialogs
        $(document).on('click', "[data-a11y-action='open-statement']", function (e) {
            e.preventDefault();
            __accessibility.openDialog('a11y-statement-dialog', this);
        });
        $(document).on('click', "[data-a11y-action='close-statement']", function (e) {
            e.preventDefault();
            __accessibility.closeDialog('a11y-statement-dialog');
        });
        $(document).on('click', "[data-a11y-action='open-complaint']", function (e) {
            e.preventDefault();
            __accessibility.openDialog('a11y-complaint-dialog', this);
        });
        $(document).on('click', "[data-a11y-action='close-complaint']", function (e) {
            e.preventDefault();
            __accessibility.closeDialog('a11y-complaint-dialog');
        });
        // Click on the backdrop (the dialog element itself, not its box) closes it.
        $(document).on('click', '.a11y-dialog', function (e) {
            if (e.target === this) {
                __accessibility.closeDialog(this.id);
            }
        });
        $(document).on('submit', '#a11y-complaint-form', function (e) {
            e.preventDefault();
            __accessibility.submitComplaint(this);
        });

        $panel.on('keydown', function (e) { __accessibility.trapFocus(e); });

        __accessibility.init();
    });
})(jQuery);
